// ---------------------------------------------------------------------------
// Which deployment the page runs on (R11e)
//
// The server writes it into every page's shell (server/html-shell.ts) as
// <meta name="sdr-deployment" content="…">, only when it is one it knows. The
// client reads it here instead of from @adornis/config. The campaign page asks
// isStaging() before its second launch confirmation on staging, where real
// messages go to real people.
// R12c: the tab title's prefix per deployment lives here too, so the shell
// (on load) and the router (on every page change, router/page-change.ts)
// write the same one; the router used to drop it.
// ---------------------------------------------------------------------------

export const DEPLOYMENTS = ['development', 'staging', 'qa', 'testing', 'production'] as const;
export type Deployment = (typeof DEPLOYMENTS)[number];

/** The name of the meta tag the shell writes. */
export const DEPLOYMENT_META = 'sdr-deployment';

/** The deployment, when it is one of ours; '' for anything else. */
export function knownDeployment(value: unknown): Deployment | '' {
  return (DEPLOYMENTS as readonly unknown[]).includes(value) ? (value as Deployment) : '';
}

/** The deployment the shell says this page runs on; '' without the tag. Read each time it is asked. */
export function deployment(): Deployment | '' {
  const meta = document.querySelector<HTMLMetaElement>(`meta[name="${DEPLOYMENT_META}"]`);
  return knownDeployment(meta?.content);
}

/** What the tab title starts with on a deployment that is not production. */
export const TITLE_PREFIX: ReadonlyMap<Deployment, string> = new Map<Deployment, string>([
  ['development', '\u{1F7E2}<DEV/> '],
  ['staging', '\u{1F534}<STAGE/> '],
  ['qa', '\u{1F7E1}<QA/> '],
]);

/** The tab title's prefix for a deployment ('' for production, testing and anything unknown). */
export function titlePrefix(value: Deployment | ''): string {
  return (value && TITLE_PREFIX.get(value)) || '';
}

/** True on staging, where a launch sends real messages to real people. */
export function isStaging(): boolean {
  return deployment() === 'staging';
}
